import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronsUp, Equal } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  StatusPill,
  TICKET_STATUS_TONES,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_TONES,
} from "@/components/StatusPill";
import { TICKET_PRIORITIES, TICKET_STATUSES } from "@/features/tickets/api/tickets.api";

const PRIORITY_ICONS: Partial<Record<string, typeof ArrowUp>> = {
  Low: ArrowDown,
  Normal: Equal,
  High: ArrowUp,
  Urgent: ChevronsUp,
};

const STATUS_DOT_CLS: Record<string, string> = {
  Open: "bg-blue-500",
  InProgress: "bg-amber-500",
  Pending: "bg-amber-500",
  Resolved: "bg-green-500",
  Closed: "bg-slate-400",
};

const PRIORITY_ICON_CLS: Record<string, string> = {
  Low: "text-blue-600",
  Normal: "text-amber-600",
  High: "text-red-600",
  Urgent: "text-red-600",
};

export function TicketStatusPill({ status }: { status: string }) {
  return (
    <StatusPill
      value={status}
      map={TICKET_STATUS_TONES}
      labels={TICKET_STATUS_LABELS}
      icon={<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
    />
  );
}

export function PriorityBadge({ priority }: { priority: string }) {
  const Icon = PRIORITY_ICONS[priority];
  return (
    <StatusPill
      value={priority}
      map={TICKET_PRIORITY_TONES}
      icon={Icon ? <Icon className="h-3 w-3 shrink-0" /> : undefined}
    />
  );
}

export const TICKET_STATUS_OPTIONS: readonly (readonly [string, ReactNode])[] =
  TICKET_STATUSES.map((s) => [
    s,
    <span className="flex items-center gap-2">
      <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT_CLS[s] ?? "bg-slate-400")} />
      {TICKET_STATUS_LABELS[s] ?? s}
    </span>,
  ]);

export const TICKET_PRIORITY_OPTIONS: readonly (readonly [string, ReactNode])[] =
  TICKET_PRIORITIES.map((p) => {
    const Icon = PRIORITY_ICONS[p];
    return [
      p,
      <span className="flex items-center gap-2">
        {Icon && <Icon className={cn("h-3.5 w-3.5", PRIORITY_ICON_CLS[p])} />}
        {p}
      </span>,
    ];
  });
